import React, { useState } from 'react';
import SPAButton from '../../shared/SPAButton';
import SPACheckbox from '../../shared/SPACheckbox';
import SPADialog from '../../shared/SPADialog';
import { useSPAToast } from '../../shared/SPAToast';
import { useDisconnectGoogle } from './hooks';
import type { UserSearchDetail, UserType } from './types';

interface DisconnectGoogleButtonProps {
  detail: UserSearchDetail;
}

const consequence = (userType: UserType, name: string) => (userType === 'staffer'
  ? `I understand ${name}'s assignments will stop syncing to Google Classroom and won't resume when they reconnect.`
  : `I understand ${name} will lose Google sign-in and Google Classroom access until they reconnect.`);

const DisconnectGoogleButton = ({ detail }: DisconnectGoogleButtonProps) => {
  const {
    first_name: firstName, google, id, last_name: lastName, subscriber, user_type: userType,
  } = detail;
  const [isOpen, setIsOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const disconnect = useDisconnectGoogle(userType, id);
  const spaToast = useSPAToast();
  const name = [firstName, lastName].filter(Boolean).join(' ') || 'this user';

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      disconnect.reset();
      setAcknowledged(false);
    }
    setIsOpen(open);
  };

  const handleConfirm = () => disconnect.mutate(undefined, {
    onSuccess: () => {
      setIsOpen(false);
      spaToast.success(`Disconnected ${name} from Google. They can reconnect by signing in with Google.`);
    },
  });

  return (
    <SPADialog open={isOpen} onOpenChange={handleOpenChange}>
      <SPADialog.Trigger asChild>
        <SPAButton size="xs" variant="danger-outline">Disconnect</SPAButton>
      </SPADialog.Trigger>
      <SPADialog.Content size="sm">
        <SPADialog.Header>
          <SPADialog.Title>Disconnect Google account</SPADialog.Title>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
          <SPADialog.Description>
            This unlinks {google?.email || 'the Google account'} from {name}
            {subscriber && ` in ${subscriber.name}`}. They can reconnect by signing in with Google again.
          </SPADialog.Description>
          <SPACheckbox
            checked={acknowledged}
            className="tw-items-start"
            onChange={event => setAcknowledged(event.target.checked)}
          >
            {consequence(userType, name)}
          </SPACheckbox>
          {disconnect.isError && (
            <p className="tw-m-0 tw-text-sm tw-leading-5 tw-text-spa-error" role="alert">
              The Google account could not be disconnected. Try again.
            </p>
          )}
        </SPADialog.Body>
        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton>Cancel</SPAButton>
          </SPADialog.Close>
          <SPAButton disabled={!acknowledged || disconnect.isLoading} variant="danger" onClick={handleConfirm}>
            Disconnect Google
          </SPAButton>
        </SPADialog.Footer>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default DisconnectGoogleButton;
